// Is each party member wired up to a Roll20 token? The Party overview shows a
// status dot per card; this module decides its colour and, for anything short
// of green, the one thing the DM should do about it.
//
// PREFER THE DURABLE KEY. A Roll20 token's OBJECT id changes every time the
// token is re-dropped (docs/ITEM4-ROLL20-EXPORT-DIAGNOSIS.md, root cause B), so
// a binding stored by object id breaks silently on re-drop — and the dot would
// go red for a token sitting right there on the map. Roll20's `represents` (the
// character id the token points at) survives a re-drop, so the match prefers it,
// then the stored object id, then the name. A stored id that no longer resolves
// but whose character/name still matches is a *stale id*, not a missing token.
//
// (Persisting `represents` onto the binding at bind time is the natural
// follow-up that closes root cause B for the bar writer too; this indicator
// already reads `represents` off the live map, so a re-drop never shows red even
// before that lands.)
//
// PURE ON PURPOSE. Nothing here reaches the bridge or the DOM — it takes the
// layout, a character, and a map snapshot and returns a status. That is what
// lets site/test/test-token-sync.mjs exercise every case with fabricated tokens,
// the same way state/campaign.ts is tested.
import type { Character, Layout } from "./types";
// Type-only: importing the VALUE side of auraReach would pull in the bridge and
// its window references, which the unit test must not load. The HP-bar read is
// done locally below instead.
import type { MapRead, MapToken } from "./auraReach";
import { barFor, pcBarLayout, type BarNo } from "./tokenBars";
import { normName as norm } from "./text";

export type SyncLevel = "green" | "yellow" | "red" | "grey";

export interface SyncStatus {
  /** the dot's colour */
  level: SyncLevel;
  /** the dot's short name: "Synced", "Needs attention", "No token", … */
  label: string;
  /** what is going on, one sentence */
  detail: string;
  /** the actionable fix — present for yellow/red, omitted when there is nothing to do */
  remedy?: string;
}

export type MatchBy = "represents" | "id" | "name" | "none";

export interface TokenMatch {
  token: MapToken | null;
  by: MatchBy;
  /** more than one token answered to the name — the DM must say which */
  ambiguous: boolean;
}

/** This character's stored Roll20 binding, if any. `represents` is optional:
 *  bindings made before the durable-key work carry only the object id and name. */
export interface StoredBinding { tokenId?: string; name?: string; represents?: string }

export function storedBinding(layout: Layout, id: string): StoredBinding | undefined {
  return ((layout.roll20Tokens || {}) as Record<string, StoredBinding>)[id];
}

/** The Roll20 game link for the active campaign, or undefined when none is set. */
export function activeRoll20Link(layout: Layout): { tabId: number; frameId: number } | undefined {
  return ((layout.roll20Links || {}) as Record<string, { tabId: number; frameId: number }>)[layout.activeCampaign];
}

/** The token on the map that is this character, preferring the durable key:
 *  `represents` (when the binding carries one) → the stored object id → an exact
 *  name → a unique first-name. The id/name tiers mirror how the connector and
 *  the bar writer find a token (state/roll20Bars.ts `characterForToken`), so the
 *  dot reflects what a real bar write would actually hit — with `represents`
 *  added in front so a re-drop does not read as a miss. */
export function findCharacterToken(layout: Layout, c: Character, tokens: MapToken[]): TokenMatch {
  const bound = storedBinding(layout, c.id);
  const aliases = (layout.whisperNames || {}) as Record<string, string>;
  // 1. the durable character id
  if (bound?.represents) {
    const t = tokens.find((x) => x.represents && x.represents === bound.represents);
    if (t) return { token: t, by: "represents", ambiguous: false };
  }
  // 2. the stored object id — breaks on re-drop, which is what makes "stale id" a case
  if (bound?.tokenId) {
    const t = tokens.find((x) => x.id === bound.tokenId);
    if (t) return { token: t, by: "id", ambiguous: false };
  }
  // 3. by name: exact, then a unique first-name ("Pryze" for "Pryze Noctivar")
  const want = norm(aliases[c.id] || bound?.name || c.name);
  if (!want) return { token: null, by: "none", ambiguous: false };
  const exact = tokens.filter((t) => norm(t.name) === want);
  if (exact.length) return { token: exact[0], by: "name", ambiguous: exact.length > 1 };
  const first = want.split(" ")[0];
  const loose = tokens.filter((t) => first.length >= 3 && norm(t.name).split(" ")[0] === first);
  if (loose.length) return { token: loose[0], by: "name", ambiguous: loose.length > 1 };
  return { token: null, by: "none", ambiguous: false };
}

const barVal = (t: MapToken, n: BarNo): string => String((n === 1 ? t.bar1 : n === 2 ? t.bar2 : n === 3 ? t.bar3 : t.bar4) ?? "");

/** Does the token carry a numeric HP bar under the party's bar layout? A token
 *  on the map with no HP bar is the "token exists but has no HP bar" case — the
 *  bars were never laid out. When the DM's layout has no HP bar at all, this is
 *  not the token's fault, so it reads as configured. (Same leading-number rule
 *  as auraReach `barNumber`, inlined to keep this module free of the bridge.) */
export function hpBarConfigured(layout: Layout, token: MapToken): boolean {
  const hp = barFor(pcBarLayout(layout), "hp");
  if (!hp) return true;
  return /^-?\d+/.test(barVal(token, hp).trim());
}

/** Everything classifySync needs, gathered from a live map read. */
export interface SyncFacts {
  name: string;
  /** the current map's name, for the message */
  pageName: string;
  /** the DM has bound or learned a token for this character */
  hasBinding: boolean;
  /** the stored object id ("" when none) */
  boundId: string;
  /** what is on the live map now */
  match: TokenMatch;
  /** a stored object id that no longer resolves, yet the character still matches
   *  by represents or name — i.e. the token was re-dropped */
  staleId: boolean;
  /** no binding at all; the only thing linking this character to a token is its name */
  nameOnly: boolean;
  /** the matched token carries an HP bar (meaningful only when a token was found) */
  hpBarOk: boolean;
}

export function collectSyncFacts(layout: Layout, c: Character, map: MapRead): SyncFacts {
  const bound = storedBinding(layout, c.id);
  const match = findCharacterToken(layout, c, map.tokens);
  const boundId = bound?.tokenId || "";
  const foundById = Boolean(boundId) && map.tokens.some((t) => t.id === boundId);
  return {
    name: c.name,
    pageName: map.pageName || "the current map",
    hasBinding: Boolean(bound?.tokenId || bound?.represents),
    boundId,
    match,
    staleId: Boolean(boundId) && !foundById && Boolean(match.token),
    nameOnly: !bound?.tokenId && !bound?.represents && match.by === "name",
    hpBarOk: match.token ? hpBarConfigured(layout, match.token) : false,
  };
}

/* THE COLOUR, AND THE ONE THING TO DO ABOUT IT.
 *
 * Which situations are a confident GREEN, which are a YELLOW worth a nudge, and
 * which are a RED that blocks sync — and the exact words of the remedy a DM
 * reads mid-fight. The thresholds here are deliberately easy to re-tune: a
 * name-only match is treated as green-with-a-nudge today (it works), but it is
 * one line to make it a yellow instead if the table wants the stronger prompt. */
export function classifySync(f: SyncFacts): SyncStatus {
  const where = f.pageName;
  // RED — nothing on the map is this character.
  if (!f.match.token) {
    return {
      level: "red",
      label: "No token",
      detail: `No token on ${where} represents ${f.name}.`,
      remedy: `Drop ${f.name}'s token on the map, then bind it with the ⇄ button on their initiative row — or re-import from Roll20.`,
    };
  }
  const named = f.match.token.name || "their token";
  // YELLOW, most-blocking first. Ambiguity first: if several tokens answer to the
  // name, the match itself is in doubt, so anything said about "the" token might
  // be about the wrong one.
  if (f.match.ambiguous) {
    return {
      level: "yellow",
      label: "Ambiguous",
      detail: `Several tokens on ${where} could be ${f.name}.`,
      remedy: `Bind the right one with ⇄, or set an exact Roll20 token name, so edits reach the token you mean.`,
    };
  }
  // A token with no HP bar: the bars were never laid out, so token HP edits have
  // nothing to flow back to the sheet through.
  if (!f.hpBarOk) {
    return {
      level: "yellow",
      label: "No HP bar",
      detail: `${f.name}'s token "${named}" is on ${where} but has no HP bar laid out.`,
      remedy: `Sync token bars to lay out HP, AC and temp HP — until then, edits to its HP on the map can't reach the sheet.`,
    };
  }
  // Re-dropped: the bound object id went stale and only the NAME is still finding
  // it. Sync keeps working, but it is one rename away from breaking, so nudge a
  // re-bind. When the match is by `represents`, the stale object id is harmless —
  // the durable key is doing the work — so that is not flagged.
  if (f.staleId && f.match.by !== "represents") {
    return {
      level: "yellow",
      label: "Re-dropped",
      detail: `${f.name} is matched to "${named}" by name — the token was re-dropped, so its bound id changed.`,
      remedy: `Re-bind with ⇄ to pin the current token; sync still works by name in the meantime.`,
    };
  }
  // GREEN — a token is found, unambiguous, with its HP bar. A name-only match
  // (never bound) still counts: it works today. The match basis goes in the
  // detail so the DM can see how durable it is.
  const how = f.match.by === "represents" ? "its Roll20 character"
    : f.match.by === "id" ? "its bound token"
    : "name";
  return {
    level: "green",
    label: "Synced",
    detail: `${f.name} is synced to "${named}" on ${where} (matched by ${how}).`,
    ...(f.nameOnly ? { remedy: `Bind with ⇄ to pin it, so a rename can't quietly break the link.` } : {}),
  };
}

/** The sync status for one party member: grey when there is nothing to sync to
 *  or the map has not been read, otherwise classified from the live map. */
export function tokenSyncStatus(layout: Layout, c: Character, map: MapRead | null): SyncStatus {
  // Nothing to sync to: this campaign has no linked Roll20 game.
  if (!activeRoll20Link(layout)) {
    const bound = storedBinding(layout, c.id);
    return {
      level: "grey",
      label: "Roll20 not linked",
      detail: `This campaign has no linked Roll20 game${bound?.tokenId ? ", though a token was bound before" : ""}.`,
      remedy: "Open the Roll20 game in this browser, then import or bind from the Initiative tab.",
    };
  }
  // Linked, but the live map has not been read yet (or the read failed): say so
  // rather than guessing green or red from a stale binding.
  if (!map || !map.ok) {
    const bound = storedBinding(layout, c.id);
    return {
      level: "grey",
      label: "Not checked",
      detail: map && map.error ? `Roll20 did not answer: ${map.error}` : "Waiting for the current Roll20 map.",
      ...(bound?.tokenId ? {} : { remedy: "Bind this character to its token (⇄) once the map is readable." }),
    };
  }
  return classifySync(collectSyncFacts(layout, c, map));
}
